import React from "react";
import {
    Box,
    FormControl,
    InputLabel,
    MenuItem,
    Select,
    SelectChangeEvent,
    TextField,
    Typography,
    Grid,
    Chip
} from "@mui/material";

const TIME_RANGES = [
    { value: 30, label: "1 Month" },
    { value: 90, label: "3 Months" },
    { value: 180, label: "6 Months" }
];

interface DateSelectorProps {
    startDate: string;
    setStartDate: (date: string) => void;
    selectedRange: number;
    setSelectedRange: (range: number) => void;
    isValidDateRange: boolean;
}

function DateSelector({
    startDate,
    setStartDate,
    selectedRange,
    setSelectedRange,
    isValidDateRange
}: DateSelectorProps) {

    const handleRangeChange = (event: SelectChangeEvent<number>) => {
        const newRange = event.target.value as number;
        setSelectedRange(newRange);

        // Auto-adjust start date based on selected range from today
        const newStartDate = new Date(Date.now() - newRange * 24 * 60 * 60 * 1000);
        setStartDate(newStartDate.toISOString().split('T')[0]);
    };

    const handleStartDateChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setStartDate(event.target.value);
    };

    const calculateEndDate = (): Date => {
        const start = new Date(startDate);
        const end = new Date(start.getTime() + selectedRange * 24 * 60 * 60 * 1000);
        return end;
    };

    return (
        <Box>
            <Box gap={2}>
                <Box mb={2}>
                    <FormControl fullWidth>
                        <InputLabel>Time Range</InputLabel>
                        <Select
                            value={selectedRange}
                            onChange={handleRangeChange}
                            label="Time Range"
                        >
                            {TIME_RANGES.map((range) => (
                                <MenuItem key={range.value} value={range.value}>
                                    {range.label}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Box>
                <Box>
                    <TextField
                        fullWidth
                        label="Start Date"
                        type="date"
                        value={startDate}
                        onChange={handleStartDateChange}
                    />
                </Box>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mt: 2 }}>
                <Chip
                    label={`End Date: ${calculateEndDate().toLocaleDateString()}`}
                    color="primary"
                    variant="outlined"
                    size="small"
                />
                <Chip
                    label={`Duration: ${selectedRange} days`}
                    color="primary"
                    variant="outlined"
                    size="small"
                />
            </Box>

            {!isValidDateRange && (
                <Typography color="error" variant="body2" sx={{ mt: 2 }}>
                    Please select a valid start date that is not in the future, and ensure the start date is before the end date.
                </Typography>
            )}
        </Box>
    );
}

export default DateSelector;